<template>
  <div class="tree-widget">
    <div class="widget-header">
      <h3 v-if="title">{{ title }}</h3>
      <button class="export-btn" @click="exportToExcel">Export XLSX</button>
    </div>
    <div class="table-scroll">
      <table class="tree-table">
        <thead>
          <tr>
            <th class="col-name">{{ rowLabel }}</th>
            <th class="col-value">{{ valueLabel }}</th>
            <th class="col-percent">% {{ valueLabel }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in visibleRows" :key="row.path" :class="'depth-' + row.depth"
              :style="{ cursor: row.hasChildren ? 'pointer' : 'default' }"
              @click="row.hasChildren && toggle(row.path)">
            <td class="col-name" :style="{ paddingLeft: (12 + row.depth * 22) + 'px' }">
              <span class="toggle-icon">{{ row.hasChildren ? (isOpen(row.path) ? '−' : '+') : '' }}</span>
              <strong v-if="row.depth === 0">{{ row.label }}</strong>
              <span v-else>{{ row.label }}</span>
            </td>
            <td class="col-value">{{ formatNum(row.total) }}</td>
            <td class="col-percent">{{ formatPct(grandTotal ? row.total / grandTotal : 0) }}</td>
          </tr>
          <tr class="total-row">
            <td class="col-name"><strong>Total</strong></td>
            <td class="col-value"><strong>{{ formatNum(grandTotal) }}</strong></td>
            <td class="col-percent"><strong>100,00%</strong></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import * as XLSX from 'xlsx';

const props = defineProps({
  title: { type: String, default: '' },
  data: { type: Array, required: true },
  // Nivells de l'arbre (de dalt a baix). Cada nivell deriva la clau/etiqueta d'una fila:
  //   { kind:'concat', fields:['name','surname'], joiner:' ' }
  //   { kind:'year',   field:'vipCosts_deliveryDate' }   → any de l'epoch ms
  //   { kind:'domain', field:'vipCosts_eventId' }        → enunciat via domainLabels
  //   { kind:'plain',  field:'vipCosts_costType' }
  levels: { type: Array, required: true },
  valueField: { type: String, default: 'count' },
  rowLabel: { type: String, default: '' },
  valueLabel: { type: String, default: 'Valor' },
  domainLabels: { type: Object, default: () => ({}) },
  euro: { type: Boolean, default: false }
});

const NO_YEAR = '(Sense data)';

// Deriva { id, label } d'una fila per a un nivell.
function keyFor(row, level) {
  if (level.kind === 'concat') {
    const parts = (level.fields || []).map((f) => row[f]).filter((v) => v != null && v !== '');
    const label = parts.join(level.joiner || ' ').trim() || 'N/A';
    return { id: label, label };
  }
  if (level.kind === 'year') {
    const ms = Number(row[level.field]);
    if (!ms || isNaN(ms)) return { id: NO_YEAR, label: NO_YEAR };
    const y = String(new Date(ms).getFullYear());
    return { id: y, label: y };
  }
  const raw = row[level.field];
  const id = (raw == null || raw === '') ? 'N/A' : String(raw);
  if (level.kind === 'domain') {
    return { id, label: props.domainLabels[id] || id };
  }
  return { id, label: id };
}

// Arbre { label, total, children:Map }. El total de cada node = suma del seu subarbre.
const tree = computed(() => {
  const root = { label: '', total: 0, children: new Map() };
  for (const row of (props.data || [])) {
    const val = Number(row[props.valueField]) || 0;
    let node = root;
    node.total += val;
    for (const level of props.levels) {
      const { id, label } = keyFor(row, level);
      if (!node.children.has(id)) node.children.set(id, { label, total: 0, children: new Map() });
      node = node.children.get(id);
      node.total += val;
    }
  }
  return root;
});

const grandTotal = computed(() => tree.value.total);

const expanded = ref({});
function isOpen(path) { return !!expanded.value[path]; }
function toggle(path) { expanded.value[path] = !expanded.value[path]; }

// Aplana l'arbre en files visibles segons l'estat obert/tancat, ordenant cada
// nivell per total descendent (els nodes amb més cost surten primer).
const visibleRows = computed(() => {
  const out = [];
  const walk = (node, depth, parentPath) => {
    const kids = Array.from(node.children.entries())
      .sort((a, b) => b[1].total - a[1].total);
    for (const [id, child] of kids) {
      const path = parentPath + '›' + id;
      out.push({ path, depth, label: child.label, total: child.total, hasChildren: child.children.size > 0 });
      if (child.children.size > 0 && isOpen(path)) walk(child, depth + 1, path);
    }
  };
  walk(tree.value, 0, '');
  return out;
});

function formatNum(val) {
  if (props.euro) {
    return Math.round(Number(val || 0)).toLocaleString('es-ES', { maximumFractionDigits: 0, useGrouping: 'always' }) + ' €';
  }
  return Number(val || 0).toLocaleString('es-ES');
}
function formatPct(val) {
  if (!val || isNaN(val)) return '0,00%';
  return (val * 100).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
}

// Export: una fila per full fulla (camí complet dels nivells + valor).
function exportToExcel() {
  const rows = [];
  const cols = props.levels.map((_, i) => 'N' + (i + 1));
  const walk = (node, trail) => {
    if (node.children.size === 0) {
      const r = {};
      cols.forEach((c, i) => { r[c] = trail[i] ?? ''; });
      r[props.valueLabel] = node.total;
      rows.push(r);
      return;
    }
    for (const child of node.children.values()) walk(child, [...trail, child.label]);
  };
  walk(tree.value, []);
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Tree');
  const safe = (props.title || 'tree').replace(/[^a-z0-9]/gi, '_').toLowerCase();
  XLSX.writeFile(wb, `${safe}.xlsx`);
}
</script>

<style scoped>
.tree-widget {
  background: white;
  border-radius: 4px;
  padding: 16px 20px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  border: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.widget-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  gap: 8px;
}

.widget-header h3 {
  font-size: 0.85rem;
  font-weight: 600;
  color: #333;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-family: 'Source Sans Pro', sans-serif;
}

.export-btn {
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid #059669;
  background-color: #10b981;
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}
.export-btn:hover { background-color: #059669; border-color: #047857; }

.table-scroll {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.tree-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.tree-table th {
  text-align: left;
  padding: 10px 8px;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
  background: #f9fafb;
  position: sticky;
  top: 0;
  z-index: 5;
}

.tree-table td {
  padding: 8px;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
}

.col-name { width: 50%; }
.col-value, .col-percent { width: 25%; text-align: right; }

.toggle-icon {
  display: inline-block;
  width: 16px;
  margin-right: 6px;
  color: #6b7280;
  font-weight: bold;
  text-align: center;
}

.depth-0 { background-color: #fafafa; }
.depth-0:hover { background-color: #f0f0f0; }
.depth-1 td, .depth-2 td { color: #4b5563; }

.total-row {
  background-color: #eff6ff;
  border-top: 2px solid #0b5394;
}
.total-row td { color: #0b5394; padding: 12px 8px; }
</style>
